/**
 * WorkspaceNavigationRegistry — Centralized registry that maps workspace
 * route patterns to contextual navigation configs.
 *
 * When the current route matches a workspace pattern the global admin
 * sidebar is replaced by a compact, operational, contextual sidebar
 * containing only the modules relevant to that workspace.
 *
 * Architecture:
 *   Global App Navigation  → SuperAdmin, Program setup, admin modules
 *   Workspace Navigation   → Patient 360, Hospital ops, HCP ops, etc.
 */

import {
  Home,
  Folder,
  RotateCw,
  FileText,
  Send,
  Phone,
  AlertTriangle,
  Activity,
  LayoutDashboard,
  MonitorCog,
  Users,
  Briefcase,
  HeartPulse,
  BarChart3,
  Package,
  ClipboardCheck,
  type LucideIcon,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────

export interface WorkspaceNavItem {
  key: string;
  label: string;
  tooltip?: string;
  icon: LucideIcon;
  /** Menu permission key — item hidden when user lacks this permission */
  menuKey?: string;
  /** Static badge count (overridden at runtime when provided) */
  badge?: number;
}

export interface WorkspaceNavConfig {
  /** Human-readable label shown at top of sidebar (e.g. "Patient Workspace") */
  title: string;
  /** When true the section title div is not rendered in the sidebar */
  hideTitle?: boolean;
  /** Route-segment builder: (programId) => base path for this workspace */
  basePath: (programId: string, ...args: string[]) => string;
  /** Navigation items */
  items: WorkspaceNavItem[];
}

export type WorkspaceKind = 'patient360' | 'hospital' | 'hcp' | 'workspace' | 'none';

// ─── Registries ──────────────────────────────────────────────

export const PATIENT_360_NAV: WorkspaceNavConfig = {
  title: 'Patient Details',
  hideTitle: true,
  basePath: (programId: string, patientId: string) =>
    `/programs/${programId}/patients/${patientId}/360`,
  items: [
    { key: 'overview',       label: 'Overview',        icon: Home },
    { key: 'cases',          label: 'Enrollment Cases', tooltip: "Represents the patient's enrollment in a support program.", icon: Folder, menuKey: 'ASSISTANCE_CASES' },
    { key: 'cycle',          label: 'Treatment Cycles', tooltip: 'Represents an individual treatment or support event within an enrollment.', icon: RotateCw },
    { key: 'documents',      label: 'Documents',       icon: FileText },
    { key: 'communications', label: 'Communications',  icon: Send },
    { key: 'call-logs',      label: 'Call Logs',        icon: Phone,          menuKey: 'CALL_LOGS' },
    { key: 'ae-pc',          label: 'AE / PC',          icon: AlertTriangle },
    { key: 'activity',       label: 'Activity',         icon: Activity },
  ],
};

/**
 * Returns the Patient360 navigation items for use in the contextual submenu.
 * This allows the main sidebar to render Patient360 items under "Patient Workspace"
 * when a patient is selected.
 */
export function getPatient360NavItems(): WorkspaceNavItem[] {
  return PATIENT_360_NAV.items;
}

/**
 * Builds the Patient360 base path for a given program and patient.
 */
export function getPatient360BasePath(programId: string, patientId: string): string {
  return PATIENT_360_NAV.basePath(programId, patientId);
}

export const HOSPITAL_WORKSPACE_NAV: WorkspaceNavConfig = {
  title: 'Hospital Workspace',
  basePath: (programId: string) => `/programs/${programId}/hospital-dashboard`,
  items: [
    { key: 'overview',          label: 'Overview',         icon: LayoutDashboard },
    { key: 'operations',        label: 'Operations',       icon: MonitorCog },
    { key: 'patients',          label: 'Patients',         icon: Users },
    { key: 'cases',             label: 'Cases',            icon: Briefcase },
    { key: 'assistance',        label: 'Assistance',       icon: HeartPulse },
    { key: 'analytics',         label: 'Analytics',        icon: BarChart3 },
    { key: 'package-requests',  label: 'Package Requests', icon: Package },
  ],
};

export const HCP_WORKSPACE_NAV: WorkspaceNavConfig = {
  title: 'HCP Workspace',
  basePath: (programId: string) => `/programs/${programId}/hcp-dashboard`,
  items: [
    { key: 'overview',          label: 'Overview',           icon: LayoutDashboard },
    { key: 'operations',        label: 'Operations',         icon: MonitorCog },
    { key: 'patient-registration-approvals', label: 'Registration Approvals', icon: ClipboardCheck },
  ],
};

// ─── Workspace route detection ───────────────────────────────

/**
 * Resolves which workspace kind (if any) the current pathname belongs to.
 * Returns 'none' for admin / non-workspace routes.
 */
export function resolveWorkspaceKind(pathname: string): WorkspaceKind {
  if (/\/patients\/[^/]+\/360(\/|$)/.test(pathname)) return 'patient360';
  if (/\/hospital-dashboard(\/|$)/.test(pathname))    return 'hospital';
  if (/\/hcp-dashboard(\/|$)/.test(pathname))         return 'hcp';
  if (/\/workspace(\/|$)/.test(pathname))             return 'workspace';
  return 'none';
}

/**
 * Returns the WorkspaceNavConfig for a given workspace kind.
 *
 * Only returns a config for workspace types that need Layout.tsx to
 * render a completely separate contextual sidebar.
 * 
 * HCP workspace now uses the unified WorkspaceSidebar component for consistency.
 * Hospital workspace still renders its own internal navigation (HospitalNavigation).
 * 
 * Patient360 now uses the unified sidebar with contextual submenu expansion,
 * so it also returns null here.
 */
export function getWorkspaceNavConfig(kind: WorkspaceKind): WorkspaceNavConfig | null {
  switch (kind) {
    // Patient360 now uses unified sidebar with contextual Patient Workspace submenu
    // case 'patient360': return PATIENT_360_NAV;
    // HCP workspace uses unified WorkspaceSidebar for consistent navigation
    case 'hcp':        return HCP_WORKSPACE_NAV;
    // hospital has its own built-in sidebar component
    default:           return null;
  }
}
